import { GroupableField } from "./pivotColumns";
import { RawAddressWithIndices, SovGridContext } from "./types";

// These types were previously in the generated client but are now owned
// here since pivots are computed entirely client-side.

// Per-row coloring tag, applied as a CSS class in PivotTable. Designed as an
// open string union so we can add new severities (e.g. "high_hazard") without
// rewiring the pipeline — only the CSS and the field-level callback change.
export type RowSeverity = "tier_1" | "tier_2" | "invalid";

export type PivotRow = {
  group_value: string;
  location_count?: number | null;
  total_tiv?: number | null;
  percent_of_tiv?: number | null;
  sub_rows?: PivotRow[] | null;
  severity?: RowSeverity | null;
};

export type PivotTableResult = {
  column_name: string;
  sub_column_name?: string | null;
  sub_percent_label?: string | null;
  rows: PivotRow[];
  total_row: PivotRow;
};

export type PivotConfig = {
  columnName: string;
  label: string;
  getValue: (
    addr: RawAddressWithIndices,
    context: SovGridContext
  ) => string | undefined | null;
  subColumnName?: string;
  subPercentLabel?: string;
  getSubValue?: (
    addr: RawAddressWithIndices,
    context: SovGridContext
  ) => string | undefined | null;
  compare?: (a: string, b: string) => number;
  // Given the addresses falling into a row, return its severity tag (or null
  // for none). Defined? → buildPivotTable retains the addresses per group so
  // it can call this; undefined → addresses are discarded as before.
  getRowSeverity?: (
    rowAddresses: RawAddressWithIndices[],
    context: SovGridContext
  ) => RowSeverity | null;
  // Same idea but for sub-rows — i.e. derived from the secondary field.
  getSubRowSeverity?: (
    rowAddresses: RawAddressWithIndices[],
    context: SovGridContext
  ) => RowSeverity | null;
};

// ---------------------------------------------------------------------------
// Hierarchical pivot (single dimension with optional sub-rows)
// ---------------------------------------------------------------------------

/**
 * Build a PivotTableResult from in-memory address data.
 */
export function buildPivotTable(
  addresses: RawAddressWithIndices[],
  config: PivotConfig,
  context: SovGridContext
): PivotTableResult {
  // Only retain per-group addresses when a severity callback is defined —
  // for the common case (no coloring) we keep the original counters-only path.
  const retainAddresses = !!config.getRowSeverity;
  const retainSubAddresses = !!config.getSubRowSeverity;
  type SubAcc = {
    count: number;
    tiv: number;
    addresses?: RawAddressWithIndices[];
  };
  type GroupAcc = {
    count: number;
    tiv: number;
    addresses?: RawAddressWithIndices[];
    subGroups?: Map<string, SubAcc>;
  };
  const groups = new Map<string, GroupAcc>();

  let totalCount = 0;
  let totalTiv = 0;

  for (const addr of addresses) {
    const raw = config.getValue(addr, context);
    const key = raw ?? "UNKNOWN";

    let group = groups.get(key);
    if (!group) {
      group = {
        count: 0,
        tiv: 0,
        addresses: retainAddresses ? [] : undefined,
        subGroups: config.getSubValue ? new Map() : undefined,
      };
      groups.set(key, group);
    }
    group.count++;
    group.tiv += addr.tiv;
    group.addresses?.push(addr);
    totalCount++;
    totalTiv += addr.tiv;

    if (config.getSubValue && group.subGroups) {
      const subRaw = config.getSubValue(addr, context);
      const subKey = subRaw ?? "UNKNOWN";
      let sub = group.subGroups.get(subKey);
      if (!sub) {
        sub = {
          count: 0,
          tiv: 0,
          addresses: retainSubAddresses ? [] : undefined,
        };
        group.subGroups.set(subKey, sub);
      }
      sub.count++;
      sub.tiv += addr.tiv;
      sub.addresses?.push(addr);
    }
  }

  const rows: PivotRow[] = Array.from(groups.entries()).map(([key, g]) => {
    const row: PivotRow = {
      group_value: key,
      location_count: g.count,
      total_tiv: g.tiv,
      percent_of_tiv: totalTiv > 0 ? (100 * g.tiv) / totalTiv : 0,
    };
    if (config.getRowSeverity && g.addresses) {
      row.severity = config.getRowSeverity(g.addresses, context);
    }
    if (g.subGroups && g.subGroups.size > 0) {
      const parentTiv = g.tiv;
      row.sub_rows = Array.from(g.subGroups.entries()).map(([subKey, sub]) => {
        const subRow: PivotRow = {
          group_value: subKey,
          location_count: sub.count,
          total_tiv: sub.tiv,
          percent_of_tiv: parentTiv > 0 ? (100 * sub.tiv) / parentTiv : 0,
        };
        if (config.getSubRowSeverity && sub.addresses) {
          subRow.severity = config.getSubRowSeverity(sub.addresses, context);
        }
        return subRow;
      });
      sortByKey(
        row.sub_rows,
        (r) => r.group_value,
        (r) => r.total_tiv ?? 0
      );
    }
    return row;
  });

  sortByKey(
    rows,
    (r) => r.group_value,
    (r) => r.total_tiv ?? 0,
    config.compare
  );

  const totalRow: PivotRow = {
    group_value: "Total",
    location_count: totalCount,
    total_tiv: totalTiv,
    percent_of_tiv: 100.0,
  };

  return {
    column_name: config.columnName,
    sub_column_name: config.subColumnName ?? null,
    sub_percent_label: config.subPercentLabel ?? null,
    rows,
    total_row: totalRow,
  };
}

// Group keys standing for "no value" — always sorted to the bottom, ahead of
// both the field comparator and the TIV fallback, so an "UNKNOWN"/"None"
// bucket never floats to the top on its TIV.
const SORT_LAST_KEYS = new Set(["UNKNOWN", "None"]);

/**
 * Sort an array in place by a string group key.
 * Sentinel "no value" keys sink to the bottom; otherwise uses the provided
 * comparator, or falls back to TIV descending.
 */
function sortByKey<T>(
  items: T[],
  getKey: (item: T) => string,
  getTiv: (item: T) => number,
  compare?: (a: string, b: string) => number
): void {
  items.sort((a, b) => {
    const keyA = getKey(a);
    const keyB = getKey(b);
    const aLast = SORT_LAST_KEYS.has(keyA);
    const bLast = SORT_LAST_KEYS.has(keyB);
    if (aLast !== bLast) return aLast ? 1 : -1;
    if (compare) return compare(keyA, keyB);
    return getTiv(b) - getTiv(a);
  });
}

/**
 * Build a PivotConfig from two GroupableFields for the hierarchical view.
 */
export function configFromFields(
  primary: GroupableField,
  secondary?: GroupableField
): PivotConfig {
  return {
    columnName: primary.key,
    label: primary.label,
    getValue: primary.getValue,
    subColumnName: secondary?.label,
    subPercentLabel: secondary ? `% of ${primary.label} TIV` : undefined,
    getSubValue: secondary?.getValue,
    compare: primary.compare,
    getRowSeverity: primary.getRowSeverity,
    getSubRowSeverity: secondary?.getRowSeverity,
  };
}

// ---------------------------------------------------------------------------
// Cross-tab (two-dimensional pivot)
// ---------------------------------------------------------------------------

export type CrossTabCell = {
  count: number;
  tiv: number;
  // Per-cell severity tag — set when either dimension defines getRowSeverity.
  // Computed from the addresses that landed in this exact (row, col) bucket,
  // so e.g. only the (TX, Harris) cell tints red, not the whole TX row.
  severity?: RowSeverity | null;
};

export type CrossTabRow = {
  rowKey: string;
  cells: Record<string, CrossTabCell>;
  total: CrossTabCell;
};

export type CrossTabResult = {
  rows: CrossTabRow[];
  columnKeys: string[];
  columnTotals: Map<string, CrossTabCell>;
  grandTotal: CrossTabCell;
};

/**
 * Build a cross-tab matrix from in-memory address data.
 * Rows = distinct values of rowField, Columns = distinct values of colField,
 * Cells = { count, tiv } at each intersection.
 */
export function buildCrossTab(
  addresses: RawAddressWithIndices[],
  rowField: GroupableField,
  colField: GroupableField,
  context: SovGridContext
): CrossTabResult {
  // Severity callback comes from whichever dimension defines one. The
  // location fields (state/county/city/wind gate) all point to the same
  // windTierSeverity helper, which reads tier per address — independent of
  // which dimension is the row vs column — so either reference works.
  const severityFn = rowField.getRowSeverity ?? colField.getRowSeverity;
  const retainAddresses = !!severityFn;

  type CellAcc = CrossTabCell & { addresses?: RawAddressWithIndices[] };
  const matrix = new Map<string, Map<string, CellAcc>>();
  const colTotals = new Map<string, CrossTabCell>();
  let grandCount = 0;
  let grandTiv = 0;

  for (const addr of addresses) {
    const rk = rowField.getValue(addr, context) ?? "UNKNOWN";
    const ck = colField.getValue(addr, context) ?? "UNKNOWN";

    let rowMap = matrix.get(rk);
    if (!rowMap) {
      rowMap = new Map();
      matrix.set(rk, rowMap);
    }

    let cell = rowMap.get(ck);
    if (!cell) {
      cell = retainAddresses
        ? { count: 0, tiv: 0, addresses: [] }
        : { count: 0, tiv: 0 };
      rowMap.set(ck, cell);
    }
    cell.count++;
    cell.tiv += addr.tiv;
    cell.addresses?.push(addr);

    let colTotal = colTotals.get(ck);
    if (!colTotal) {
      colTotal = { count: 0, tiv: 0 };
      colTotals.set(ck, colTotal);
    }
    colTotal.count++;
    colTotal.tiv += addr.tiv;

    grandCount++;
    grandTiv += addr.tiv;
  }

  const columnKeys = Array.from(colTotals.keys());
  sortByKey(
    columnKeys,
    (k) => k,
    (k) => colTotals.get(k)?.tiv ?? 0,
    colField.compare
  );

  const rows: CrossTabRow[] = Array.from(matrix.entries()).map(
    ([rowKey, rowMap]) => {
      const cells: Record<string, CrossTabCell> = {};
      let rowCount = 0;
      let rowTiv = 0;
      for (const [ck, cell] of rowMap) {
        rowCount += cell.count;
        rowTiv += cell.tiv;
        const finalCell: CrossTabCell = { count: cell.count, tiv: cell.tiv };
        if (severityFn && cell.addresses) {
          finalCell.severity = severityFn(cell.addresses, context);
        }
        cells[ck] = finalCell;
      }
      return {
        rowKey,
        cells,
        total: { count: rowCount, tiv: rowTiv },
      };
    }
  );

  sortByKey(
    rows,
    (r) => r.rowKey,
    (r) => r.total.tiv,
    rowField.compare
  );

  return {
    rows,
    columnKeys,
    columnTotals: colTotals,
    grandTotal: { count: grandCount, tiv: grandTiv },
  };
}
